import { useState, useEffect, useMemo } from "react";
import { Editor } from "react-draft-wysiwyg";
import { EditorState, convertToRaw, convertFromHTML, ContentState } from "draft-js";
import draftToHtml from "draftjs-to-html";
import "react-draft-wysiwyg/dist/react-draft-wysiwyg.css";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";

import { dispatch } from "../../state/store";
import { setIsNeedToResetTextEditorState } from "../../state/ducks";

import undoIcon from "../../assets/images/TextEditor/undo.svg";
import redoIcon from "../../assets/images/TextEditor/redo.svg";

import boldIcon from "../../assets/images/TextEditor/bold.svg";
import italicIcon from "../../assets/images/TextEditor/italic.svg";
import underlineIcon from "../../assets/images/TextEditor/underline.svg";
import strikethroughIcon from "../../assets/images/TextEditor/strikethrough.svg";

import linkIcon from "../../assets/images/TextEditor/link.svg";

import orderedIcon from "../../assets/images/TextEditor/ordered.svg";
import unorderedIcon from "../../assets/images/TextEditor/unordered.svg";

import { strCode } from "../../consts";
import { t } from "../../services";
import { useDebounce } from "../../hooks";

import { TextEditorWrapper } from "./styled";
import { Paragraph2 } from "../Text/Paragraphs";

const toolbar = {
  options: ["history", "blockType", "inline", "link", "list"],
  history: {
    undo: { icon: undoIcon },
    redo: { icon: redoIcon }
  },
  blockType: {
    inDropdown: true,
    className: "blockType",
    options: ["Normal", "H1", "H2", "H3", "H4", "H5", "H6"],
    dropdownClassName: "blockType-dropdown"
  },
  inline: {
    inDropdown: false,
    options: ["bold", "italic", "underline", "strikethrough"],
    bold: { icon: boldIcon },
    italic: { icon: italicIcon },
    underline: { icon: underlineIcon },
    strikethrough: { icon: strikethroughIcon }
  },
  link: {
    options: ["link"],
    link: { icon: linkIcon }
  },
  list: {
    options: ["unordered", "ordered"],
    ordered: { icon: orderedIcon },
    unordered: { icon: unorderedIcon }
  }
};

const editorLabels = {
  "components.controls.blocktype.h1": "Heading 1",
  "components.controls.blocktype.h2": "Heading 2",
  "components.controls.blocktype.h3": "Heading 3",
  "components.controls.blocktype.h4": "Heading 4",
  "components.controls.blocktype.h5": "Heading 5",
  "components.controls.blocktype.h6": "Heading 6",
  "components.controls.blocktype.normal": "Paragraph"
};

const getBlockStyle = (block) => {
  switch (block.getType()) {
    case "unstyled":
      return "Paragraph";
    case "header-one":
      return "Header 1";
    default:
      return null;
  }
};

const convertHtmlToEditorState = (htmlMarkup) => {
  let editorState = EditorState.createEmpty();

  if (htmlMarkup) {
    const blocksFromHTML = convertFromHTML(htmlMarkup);
    const state = ContentState.createFromBlockArray(
      blocksFromHTML.contentBlocks,
      blocksFromHTML.entityMap
    );

    editorState = EditorState.createWithContent(state);
  }

  return EditorState.moveSelectionToEnd(editorState);
};

const convertEditorStateToHtml = (editorState) => {
  if (editorState) {
    const htmlMarkup = draftToHtml(convertToRaw(editorState.getCurrentContent()));
    return htmlMarkup;
  }
};

const TextEditor = ({
  title,
  initialHtmlMarkup,
  selectedId,
  onChange,
  hasError,
  viewOnly,
  placeholder,
  small,
  disabledHeaderVisible
}) => {
  const [text, setText] = useState(convertHtmlToEditorState());

  const isNeedToResetTextEditorState = useSelector(
    (store) => store.configs.isNeedToResetTextEditorState
  );

  const saveText = () => {
    const htmlMarkup = convertEditorStateToHtml(text);
    onChange(htmlMarkup);
  };

  useDebounce(saveText, 100, text);

  useEffect(() => {
    if (initialHtmlMarkup !== null) {
      setText(convertHtmlToEditorState(initialHtmlMarkup));
    }
  }, [initialHtmlMarkup, selectedId]);

  useEffect(() => {
    if (isNeedToResetTextEditorState) {
      setText(convertHtmlToEditorState(initialHtmlMarkup));
      dispatch(setIsNeedToResetTextEditorState(false));
    }
  }, [isNeedToResetTextEditorState]);

  const toolbarOptions = useMemo(() => {
    if (!small) {
      return toolbar;
    }

    return {
      ...toolbar,
      blockType: {
        ...toolbar.blockType,
        options: ["Normal", "H1", "H2"]
      }
    };
  }, [small]);

  return (
    <TextEditorWrapper
      hasError={hasError}
      viewOnly={viewOnly}
      small={small}
      disabledHeaderVisible={disabledHeaderVisible}>
      {title && <Paragraph2>{title}</Paragraph2>}

      <Editor
        editorState={text}
        readOnly={viewOnly}
        wrapperClassName="wrapper"
        toolbarClassName="toolbar"
        editorClassName={"editor"}
        onEditorStateChange={setText}
        blockStyleFn={getBlockStyle}
        toolbar={toolbarOptions}
        placeholder={placeholder || t(strCode.TEXT_EDITOR_PLACEHOLDER)}
        localization={{ locale: "en", translations: editorLabels }}
        stripPastedStyles
      />
    </TextEditorWrapper>
  );
};

TextEditor.propTypes = {
  title: PropTypes.string,
  initialHtmlMarkup: PropTypes.string,
  selectedId: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  hasError: PropTypes.bool,
  viewOnly: PropTypes.bool.isRequired,
  placeholder: PropTypes.string,
  small: PropTypes.string,
  disabledHeaderVisible: PropTypes.bool
};

export default TextEditor;
